import { useState } from 'react'
import { PinPad } from '@/components/ui/pin-pad'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Ban, CheckCircle, Minus, Plus, Printer, X } from 'lucide-react'
import { useCurrency } from '@/contexts/CurrencyContext'
import type { StationKOT } from '@/types'
import { printKotReceipts } from '@/lib/printKotReceipt'
import { parseReceiptSettings } from '@/lib/printCustomerReceipt'
import { isAndroidPrintClient } from '@/lib/bhooklyAppPrint'
import { shouldRelayCounterPrint, watchRelayedPrintJobs } from '@/lib/counterRelay'
import { isBhooklyNativeServerApp } from '@/lib/bhooklyNative'
import { printDebug } from '@/lib/printDebug'
import {
  OTHER_CODE,
  VOID_NOTE_MAX,
  VOID_OUTCOME_HELP,
  VOID_OUTCOME_LABEL,
  composeOutcomeReason,
  outcomePresets,
  suggestedVoidOutcome,
  type VoidOutcome,
} from '@/lib/void-reasons'
import { messageForVoidFailure } from '@/lib/void-pin-error'

/** One line of a whole-order void, as the counter shows it. */
export interface WholeOrderVoidLine {
  id: string
  name: string
  quantity: number
  /** The line's billed amount (options and line discount included). */
  amount: number
}

interface PinEntryModalProps {
  orderId: string
  itemId: string
  itemName: string
  quantity: number
  unitPrice: number
  /** The line's kitchen status; pre-selects "made" once cooking started. */
  itemStatus?: string
  onSuccess: (itemId: string) => void
  onClose: () => void
  /**
   * F-FE-N193: the reason chip to start on when the chosen outcome offers it
   * (the online-orders board passes 'out_of_stock'). Falls back to the
   * outcome's first chip.
   */
  defaultReasonCode?: string
  /** Called with the server's reply once a single-item void succeeds. */
  onVoided?: (data: unknown) => void
  /**
   * Whole-order mode: one reason and one PIN void every listed line in turn
   * (a comped table, an order rung up by mistake). The single-item props are
   * ignored. Each line still goes through VoidItem, so each gets its own
   * void_log row, stock reversal or waste write-off, and the backend still
   * closes or re-seats the order once the last line goes. No VOID chits are
   * printed — this is for food that was served or never wanted, not a ticket
   * the kitchen must pull; the KDS still strikes the lines over SSE.
   */
  wholeOrder?: {
    /** What is being voided, e.g. "Order #20261009-002" or "Table 4". */
    label: string
    lines: WholeOrderVoidLine[]
  }
}

export function PinEntryModal({
  orderId,
  itemId,
  itemName,
  quantity,
  unitPrice,
  itemStatus,
  onSuccess,
  onClose,
  wholeOrder,
  defaultReasonCode,
  onVoided,
}: PinEntryModalProps) {
  // The chip an outcome starts on: the caller's preferred reason if that
  // outcome offers it, else its first chip.
  const startChip = (o: VoidOutcome) => {
    const chips = outcomePresets(o)
    return chips.find((r) => r.code === defaultReasonCode)?.code ?? chips[0].code
  }
  const { formatCurrency } = useCurrency()
  const queryClient = useQueryClient()
  const [pin, setPin] = useState('')
  // F-CTRL-N74: "was it made?" comes first; the reasons depend on the answer.
  // Whole-order mode never guesses: a comped meal was made, a mistaken ring-up
  // was not, and the manager must say which.
  const [outcome, setOutcome] = useState<VoidOutcome | null>(() =>
    wholeOrder ? null : suggestedVoidOutcome(itemStatus),
  )
  const [preset, setPreset] = useState<string | null>(() => {
    const o = wholeOrder ? null : suggestedVoidOutcome(itemStatus)
    return o ? startChip(o) : null
  })
  // Whole-order progress. doneIds survives a failed run so a second tap
  // voids only what is left.
  const [doneIds, setDoneIds] = useState<Set<string>>(() => new Set())
  const [wholeRunning, setWholeRunning] = useState(false)
  const [wholeAt, setWholeAt] = useState(0)
  const wholeLines = wholeOrder?.lines ?? []
  const remainingLines = wholeLines.filter((l) => !doneIds.has(l.id))
  const wholeTotal = wholeLines.reduce((s, l) => s + l.amount, 0)
  const [custom, setCustom] = useState('')
  const [voidQty, setVoidQty] = useState(quantity > 0 ? 1 : 0)
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  // A VOID chit that needs a tap to print (confirm-before-print venue, or an
  // Android PWA where intent:// must be launched from a user gesture). When
  // set, the success panel waits for the tap instead of auto-dismissing.
  const [pendingVoidChit, setPendingVoidChit] = useState<{
    kot: StationKOT
    cfg: ReturnType<typeof parseReceiptSettings>
  } | null>(null)
  const [shake, setShake] = useState(false)

  // Typing beats tapping: the moment there is text in the free-text field it
  // IS the reason, and the preset chips deselect. One field, one answer — no
  // state where a chip and a typed reason disagree about what gets filed.
  const usingCustom = custom.trim() !== ''
  // Defensive only: clearing the field restores the default chip, so today
  // there is no path to "no reason at all". It stays as a guard because the
  // day someone removes the preselected default, a void with a blank reason
  // must not be the thing that ships.
  const reasonMissing = !usingCustom && preset === null
  const outcomeMissing = outcome === null
  const reasonCode = usingCustom ? OTHER_CODE : (preset ?? '')
  const effectiveReason = outcome ? composeOutcomeReason(outcome, reasonCode, custom) : ''
  const presets = outcome ? outcomePresets(outcome) : []

  const chooseOutcome = (o: VoidOutcome) => {
    setOutcome(o)
    // Codes differ per outcome, so a chip from the other list can't carry over.
    setPreset(usingCustom ? null : startChip(o))
    setError('')
  }


  const voidMutation = useMutation({
    mutationFn: async () => {
      const pinStr = pin
      return apiClient.voidItem(orderId, itemId, {
        pin: pinStr,
        reason: effectiveReason,
        outcome: outcome ?? undefined,
        reason_code: reasonCode || undefined,
        quantity: voidQty < quantity ? voidQty : undefined,
        // F-FE-N60: a "DO NOT MAKE" chit is the one slip that must never be
        // lost. On a tablet, relay it to the counter rather than firing an
        // intent:// at a printer this device cannot reach.
        relay_counter_print: shouldRelayCounterPrint() || undefined,
      })
    },
    onSuccess: async (res) => {
      if (!res.success) {
        setError(res.message?.trim() || 'Void failed. Try again.')
        setShake(true)
        setTimeout(() => setShake(false), 500)
        return
      }
      onVoided?.(res.data)
      const authorizedBy = (res.data as any)?.authorized_by || 'Manager'
      setSuccessMsg(
        outcome === 'wasted'
          ? `Voided ${voidQty} × ${itemName} by ${authorizedBy}. Recorded as waste.`
          : `Voided ${voidQty} × ${itemName} by ${authorizedBy}`,
      )

      // Strike-off chit: the backend returns the VOID KOT routed to the
      // station the line was fired to. Printer stations get paper; KDS
      // stations already strike the line on the board via the SSE `voided`
      // event, so nothing prints for them. Same gating as the fire path
      // (CounterInterface): auto-print unless the venue confirms every KOT
      // print, or this is an Android PWA where intent:// needs a tap.
      void watchRelayedPrintJobs((res.data as any)?.relayed_print_jobs)

      const voidKot = (res.data as any)?.void_kot as StationKOT | null | undefined
      // A relayed chit is already queued for the counter — printing it here too
      // would put the same "DO NOT MAKE" on two printers, and on a PWA it would
      // also park the modal on a "tap to print" the waiter cannot satisfy.
      if (voidKot && !voidKot.relayed && voidKot.output_type === 'printer') {
        try {
          const settingsRes = await queryClient.fetchQuery({
            queryKey: ['settings', 'all'],
            queryFn: () => apiClient.getAllSettings(),
          })
          const sd = (settingsRes.data ?? {}) as Record<string, unknown>
          const cfg = parseReceiptSettings(sd)
          const confirmPrint =
            sd['kitchen.confirm_kot_print'] === true || sd['kitchen.confirm_kot_print'] === 'true'
          const phoneNeedsGesture = isAndroidPrintClient() && !isBhooklyNativeServerApp()
          if (confirmPrint || phoneNeedsGesture) {
            setPendingVoidChit({ kot: voidKot, cfg })
            return // wait for the tap — no auto-dismiss
          }
          void printKotReceipts([voidKot], cfg)
        } catch (e) {
          printDebug.error('kot', 'void chit print failed', e)
        }
      }
      setTimeout(() => {
        onSuccess(itemId)
      }, 1200)
    },
    onError: (err: unknown) => {
      const e = err as {
        code?: string
        status?: number
        isNetworkError?: boolean
        message?: string
      }
      setError(messageForVoidFailure(e))
      setShake(true)
      // Only a genuine PIN miss should wipe the pad. A 500 on a qty>1 line
      // (Mizaj 20260906-009) must not send the operator back into PIN entry.
      if (e.status === 403 || e.code === 'invalid_manager_pin') {
        setPin('')
      }
      setTimeout(() => setShake(false), 500)
    },
  })

  const pending = wholeOrder ? wholeRunning : voidMutation.isPending

  // Whole-order void: the same VoidItem call, once per line, in order. Stops
  // at the first failure — a PIN miss fails the first line and voids nothing;
  // a dropped connection mid-way leaves the rest for the next tap.
  const runWholeOrderVoid = async () => {
    if (!wholeOrder || wholeRunning) return
    setWholeRunning(true)
    setError('')
    const done = new Set(doneIds)
    let authorizedBy = 'Manager'
    try {
      for (const line of wholeLines) {
        if (done.has(line.id)) continue
        setWholeAt(done.size + 1)
        try {
          const res = await apiClient.voidItem(orderId, line.id, {
            pin,
            reason: effectiveReason,
            outcome: outcome ?? undefined,
            reason_code: reasonCode || undefined,
          })
          if (!res.success) throw { message: res.message }
          authorizedBy = (res.data as any)?.authorized_by || authorizedBy
        } catch (err) {
          const e = err as { code?: string; status?: number; isNetworkError?: boolean; message?: string }
          // Someone else got there first; the line is gone either way.
          if (e.code !== 'item_already_voided') {
            setDoneIds(new Set(done))
            const why = messageForVoidFailure(e)
            setError(
              done.size > 0
                ? `Voided ${done.size} of ${wholeLines.length}. ${why} Tap again to finish.`
                : why,
            )
            setShake(true)
            if (e.status === 403 || e.code === 'invalid_manager_pin') setPin('')
            setTimeout(() => setShake(false), 500)
            return
          }
        }
        done.add(line.id)
      }
      setDoneIds(new Set(done))
      setSuccessMsg(
        outcome === 'wasted'
          ? `Voided ${wholeLines.length} items by ${authorizedBy}. Recorded as waste.`
          : `Voided ${wholeLines.length} items by ${authorizedBy}.`,
      )
      setTimeout(() => onSuccess(''), 1200)
    } finally {
      setWholeRunning(false)
    }
  }

  // Closing after a partial whole-order run must still refresh the order, or
  // the counter keeps showing lines that are already voided.
  const close = () => {
    if (wholeOrder && doneIds.size > 0) onSuccess('')
    else onClose()
  }

  const handleSubmit = () => {
    if (outcomeMissing) {
      setError('Say whether it was made')
      return
    }
    if (reasonMissing) {
      setError('Pick a reason or type your own')
      return
    }
    const pinStr = pin
    if (pinStr.length !== 4) {
      setError('Please enter a 4-digit PIN')
      return
    }
    if (wholeOrder) void runWholeOrderVoid()
    else voidMutation.mutate()
  }

  if (successMsg) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
        <div className="bg-popover text-popover-foreground rounded-2xl p-8 w-96 max-w-full max-h-[calc(var(--app-vh,100dvh)_-_1.5rem)] overflow-y-auto text-center shadow-2xl">
          <div className="w-16 h-16 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h3 className="text-xl font-bold text-foreground">Item Voided</h3>
          <p className="text-muted-foreground mt-2">{successMsg}</p>
          {pendingVoidChit && (
            <div className="mt-6 space-y-2">
              <p className="text-sm text-muted-foreground">
                Print the VOID chit for <strong>{pendingVoidChit.kot.station_name}</strong> so the kitchen
                pulls the ticket.
              </p>
              <Button
                type="button"
                className="w-full"
                onClick={() => {
                  void printKotReceipts([pendingVoidChit.kot], pendingVoidChit.cfg)
                  setPendingVoidChit(null)
                  onSuccess(itemId)
                }}
              >
                <Printer className="w-4 h-4 mr-2" />
                Print void chit
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setPendingVoidChit(null)
                  onSuccess(itemId)
                }}
              >
                Done without printing
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
      {/*
        F-FE-N108: the card must be able to SCROLL, and must be measured
        against the real viewport height.

        This was `overflow-hidden` with no height cap. On a tablet held in
        LANDSCAPE the viewport is ~500px tall and this card — reason list, PIN
        pad, then the actions — is taller than that. A centred flex child that
        overflows its container spills equally past the top and the bottom, so
        neither end can be reached, and `overflow-hidden` then clipped the
        buttons outright. The cashier saw the reasons and the keypad and had no
        Void button and nothing to scroll. Portrait was always fine, which is
        why it survived every test.

        `--app-vh` rather than plain dvh: some Android tablet browsers keep
        resolving dvh against the PRE-ROTATION viewport, so on the one
        orientation that is broken dvh reports the tall portrait height and the
        cap does nothing. AppViewportVar republishes the real height on every
        resize; the dvh fallback covers the first paint and the desktop.
      */}
      <div className="bg-popover text-popover-foreground rounded-2xl w-96 max-w-full max-h-[calc(var(--app-vh,100dvh)_-_1.5rem)] overflow-y-auto overflow-x-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-destructive/10 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Ban className="w-5 h-5 text-destructive" />
            <h3 className="font-bold text-destructive">{wholeOrder ? 'Void whole order' : 'Void Item'}</h3>
          </div>
          <button onClick={close} disabled={pending} className="text-muted-foreground hover:text-foreground disabled:opacity-40">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Item info */}
        {wholeOrder ? (
          <div className="p-4 border-b">
            <div className="font-medium text-foreground">
              {wholeOrder.label} · {wholeLines.length} item{wholeLines.length === 1 ? '' : 's'}
            </div>
            <ul className="mt-2 max-h-36 space-y-1 overflow-y-auto pr-1 text-sm">
              {wholeLines.map((l) => (
                <li
                  key={l.id}
                  className={`flex justify-between gap-3 ${doneIds.has(l.id) ? 'text-muted-foreground line-through' : 'text-foreground'}`}
                >
                  <span className="min-w-0 truncate">
                    {l.quantity} &times; {l.name}
                  </span>
                  <span className="shrink-0 tabular-nums">{formatCurrency(l.amount)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-2 text-sm font-medium text-destructive">
              Voiding: {formatCurrency(wholeTotal)}
            </div>
          </div>
        ) : (
          <div className="p-4 border-b">
            <div className="font-medium text-foreground">{itemName}</div>
            <div className="text-sm text-muted-foreground mt-1">
              Qty: {quantity} &times; {formatCurrency(unitPrice)} = {formatCurrency(quantity * unitPrice)}
            </div>
          </div>
        )}

        {/* Quantity selector -- only shown when item has qty > 1 */}
        {!wholeOrder && quantity > 1 && (
          <div className="p-4 border-b">
            <label className="text-sm font-medium text-foreground block mb-2">
              Void how many?
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setVoidQty((v) => Math.max(1, v - 1))}
                disabled={voidQty <= 1}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-foreground transition-colors hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="min-w-[3rem] text-center text-lg font-bold tabular-nums">
                {voidQty}
              </span>
              <button
                type="button"
                onClick={() => setVoidQty((v) => Math.min(quantity, v + 1))}
                disabled={voidQty >= quantity}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-foreground transition-colors hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="h-4 w-4" />
              </button>
              <span className="text-sm text-muted-foreground">
                of {quantity}
              </span>
            </div>
            <div className="mt-1.5 text-sm font-medium text-destructive">
              Voiding: {formatCurrency(unitPrice * voidQty)}
            </div>
          </div>
        )}

        {/* F-CTRL-N74: was it made? */}
        <div className="p-4 border-b">
          <label className="text-sm font-medium text-foreground block mb-2">Was it made?</label>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Was it made?">
            {(['not_made', 'wasted'] as const).map((o) => {
              const selected = outcome === o
              return (
                <button
                  key={o}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => chooseOutcome(o)}
                  className={`rounded-lg border px-3 py-2 text-sm text-left transition-colors ${
                    selected
                      ? 'border-destructive bg-destructive/10 text-destructive font-medium'
                      : 'border-input bg-background text-foreground hover:bg-accent'
                  }`}
                >
                  {VOID_OUTCOME_LABEL[o]}
                </button>
              )
            })}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            {outcome
              ? VOID_OUTCOME_HELP[outcome]
              : 'Not made: the customer changed their mind in time. Made: it was cooked and has to be thrown away.'}
          </p>
        </div>

        {/* Reason */}
        {outcome && (
        <div className="p-4 border-b">
          <label className="text-sm font-medium text-foreground block mb-2">Reason</label>
          <div className="grid grid-cols-2 gap-2">
            {presets.map((r, i) => {
              const selected = !usingCustom && preset === r.code
              // An odd count would strand the last chip in the left column;
              // let it span instead so the block ends on a clean edge.
              const spans = i === presets.length - 1 && presets.length % 2 === 1
              return (
                <button
                  key={r.code}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    setPreset(r.code)
                    setCustom('')
                    setError('')
                  }}
                  className={`rounded-lg border px-3 py-2 text-sm text-left transition-colors ${
                    spans ? 'col-span-2' : ''
                  } ${
                    selected
                      ? 'border-destructive bg-destructive/10 text-destructive font-medium'
                      : 'border-input bg-background text-foreground hover:bg-accent'
                  }`}
                >
                  {r.label}
                </button>
              )
            })}
          </div>

          {/* The last option is the field itself — no "Other" to tap first. */}
          <div className="mt-2">
            <input
              type="text"
              value={custom}
              maxLength={VOID_NOTE_MAX}
              onChange={e => {
                setCustom(e.target.value)
                // Typing IS choosing; drop the chip so only one reason is live.
                if (e.target.value.trim() !== '') setPreset(null)
                else setPreset(presets[0]?.code ?? null)
                setError('')
              }}
              placeholder="Or type your own reason…"
              className={`w-full rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring ${
                usingCustom
                  ? 'border-destructive bg-destructive/10 text-foreground'
                  : 'border-input bg-background text-foreground'
              }`}
            />
            {usingCustom && (
              <div className="mt-1.5 flex items-start justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  Saved to the void log — write what you would want to read back later.
                </p>
                <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                  {custom.length}/{VOID_NOTE_MAX}
                </span>
              </div>
            )}
          </div>
        </div>
        )}

        {/* PIN Input */}
        <div className="p-6">
          <label className="text-sm font-medium text-foreground block mb-3 text-center">
            Enter Manager PIN
          </label>
          {/* What the manager is approving, in one line. */}
          {outcome && (
            <p className="-mt-1 mb-3 text-center text-xs text-muted-foreground">
              {wholeOrder
                ? `Void ${remainingLines.length} item${remainingLines.length === 1 ? '' : 's'}`
                : `Void ${voidQty} × ${itemName}`}{' '}
              — {VOID_OUTCOME_LABEL[outcome].toLowerCase()}
            </p>
          )}
          <PinPad
            value={pin}
            onChange={setPin}
            onSubmit={handleSubmit}
            invalid={shake}
            disabled={pending}
          />
          {error && (
            <p className="text-destructive text-sm text-center mt-3">{error}</p>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t flex gap-3">
          <Button variant="outline" className="flex-1" onClick={close} disabled={wholeRunning}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            onClick={handleSubmit}
            disabled={pin.length !== 4 || outcomeMissing || reasonMissing || pending}
          >
            {wholeOrder
              ? wholeRunning
                ? `Voiding ${wholeAt} of ${wholeLines.length}…`
                : `Void ${remainingLines.length} item${remainingLines.length === 1 ? '' : 's'}`
              : voidMutation.isPending
                ? 'Verifying...'
                : `Void ${voidQty === quantity ? 'All' : voidQty}`}
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          75% { transform: translateX(8px); }
        }
        .animate-shake { animation: shake 0.3s ease-in-out 2; }
      `}</style>
    </div>
  )
}
